import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Kansas = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Kansas Form 2290 Filing Guide | E-file HVUT with TaxZerone"
          description="Learn how to file Form 2290 in Kansas, get your stamped Schedule 1, and stay compliant. E-file quickly and securely with TaxZerone to avoid penalties."
          keywords=""
          canonical="resources/excise-tax/kansas-form-2290/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Kansas</span>
          </span>{' '}
          Form 2290 for Truck Owners and Operators
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File Your Heavy Highway Use Tax with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=kansas-form-2290`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 2290?
                </Typography> */}
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/kansas-form-2290/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        Kansas Form 2290 at a Glance
                      </Typography>

                      {/* Main List - All 3 items in a straight line */}
                      <Box
                        component="ul"
                        itemProp="articleBody"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        {/* Item 1 */}
                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Form 2290
                            </strong>{' '}
                            must be filed by Kansas truck owners and operators
                            of heavy highway vehicles with a taxable gross
                            weight of{' '}
                            <strong style={{ fontWeight: 600 }}>
                              55,000 pounds or more
                            </strong>{' '}
                            to report and pay the{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Heavy Vehicle Use Tax (HVUT)
                            </strong>
                            . The IRS-stamped{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Schedule 1
                            </strong>{' '}
                            serves as proof of HVUT payment and may be required
                            for vehicle registration or renewal in Kansas.
                          </span>
                        </li>

                        {/* Item 2 */}
                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Due Date:
                            </strong>{' '}
                            Form 2290 must be filed by the{' '}
                            <strong style={{ fontWeight: 600 }}>
                              last day of the month following the vehicle's
                              First Used Month (FUM)
                            </strong>
                            . For vehicles first used in{' '}
                            <strong style={{ fontWeight: 600 }}>
                              July 2026
                            </strong>
                            , the filing deadline is{' '}
                            <strong style={{ fontWeight: 600 }}>
                              August 31, 2026
                            </strong>
                            .
                          </span>
                        </li>

                        {/* Item 3 */}
                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Penalties:
                            </strong>{' '}
                            Late filing may result in a{' '}
                            <strong style={{ fontWeight: 600 }}>
                              4.5% penalty
                            </strong>{' '}
                            of the unpaid tax, while late payment may incur an
                            additional{' '}
                            <strong style={{ fontWeight: 600 }}>
                              0.5% monthly penalty
                            </strong>
                            , plus applicable interest. Failure to provide proof
                            of HVUT payment may also cause{' '}
                            <strong style={{ fontWeight: 600 }}>
                              registration or renewal delays
                            </strong>{' '}
                            in Kansas.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>
                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      If you own or operate a heavy vehicle in Kansas, you're
                      likely required to pay the{' '}
                      <span style={{ fontWeight: 600 }}>
                        Heavy Vehicle Use Tax (HVUT)
                      </span>
                      annually using{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        IRS Form 2290
                      </Link>
                      . This federal excise tax applies to vehicles that weigh{' '}
                      <span style={{ fontWeight: 600 }}>
                        55,000 pounds or more
                      </span>{' '}
                      and travel on public highways. Though HVUT is a federal
                      tax, Kansas truckers must file it to stay compliant and
                      register their vehicles with the{' '}
                      <span style={{ fontWeight: 600 }}>
                        Kansas Department of Revenue (DOR).
                      </span>
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      This page provides a complete overview of the Kansas HVUT
                      Form, including when, why, and how to file it.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>

                  <List
                    sx={{
                      mb: 5,
                      border: '1px solid #ccc',
                      borderRadius: 2,
                    }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is the Heavy Vehicle Use Tax (HVUT)?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file HVUT in Kansas?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why Kansas Truckers Need Form 2290?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When is the HVUT Due?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to File HVUT in Kansas?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where to Submit Proof of HVUT Payment?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pay" sx={{ color: '#0202ff' }}>
                          How to Pay Kansas Highway Use Tax?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#penalties" sx={{ color: '#0202ff' }}>
                          Kansas HVUT Penalties
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#consequences" sx={{ color: '#0202ff' }}>
                          Kansas State Consequences
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What is the Heavy Vehicle Use Tax (HVUT)?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The{' '}
                  <span style={{ fontWeight: 600 }}>
                    Heavy Vehicle Use Tax (HVUT)
                  </span>{' '}
                  is a <span style={{ fontWeight: 600 }}>federal tax</span>{' '}
                  imposed on heavy vehicles operating on public highways with a{' '}
                  <span style={{ fontWeight: 600 }}>
                    gross weight of 55,000 pounds or more
                  </span>
                  . The tax is filed annually using{' '}
                  <span style={{ fontWeight: 600 }}>IRS Form 2290</span>, and
                  the collected funds help maintain and repair the nation's
                  highway system.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 3, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Ready to File Your Kansas HVUT?
                      </Typography>
                      <Typography variant="body2">
                        E-file Form 2290 with TaxZerone and get your stamped
                        Schedule 1 instantly. Stay compliant and keep your truck
                        on the road!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 190, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=kansas-form-2290`}
                      >
                        File Form 2290 Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who must file HVUT in Kansas?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You are required to file HVUT if:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    You own or operate a taxable vehicle with a gross weight of{' '}
                    <span style={{ fontWeight: 600 }}>55,000 pounds</span> or
                    more.
                  </li>
                  <li>
                    Your vehicle is registered in Kansas or based in Kansas for{' '}
                    <Link
                      href="/resources/excise-tax/irp/"
                      sx={{ color: '#0202ff' }}
                    >
                      IRP
                    </Link>{' '}
                    or{' '}
                    <Link
                      href="/resources/excise-tax/international-fuel-tax-agreement/"
                      sx={{ color: '#0202ff' }}
                    >
                      IFTA
                    </Link>
                    .
                  </li>
                  <li>
                    You intend to operate that vehicle on public highways during
                    the tax period.
                  </li>
                  <li>
                    You want to register or renew a vehicle registration in
                    Kansas.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This applies to:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <Link
                      href="https://owneroperator2290.com/?ref=taxzerone"
                      sx={{ color: '#0202ff' }}
                    >
                      Owner-operators
                    </Link>
                  </li>
                  <li>Fleet owners</li>
                  <li>Agricultural truckers</li>
                  <li>Kansas-based trucking companies</li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 3,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="why"
                >
                  Why Kansas Truckers Need Form 2290?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Although HVUT is a federal tax,{' '}
                  <span style={{ fontWeight: 600 }}>
                    Kansas requires proof of payment
                  </span>{' '}
                  before it allows you to:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Register or{' '}
                    <span style={{ fontWeight: 600 }}>
                      renew your registration
                    </span>{' '}
                    of heavy vehicles.{' '}
                  </li>
                  <li>
                    Obtain or renew{' '}
                    <span style={{ fontWeight: 600 }}>apportioned plates</span>{' '}
                    under the{' '}
                    <span style={{ fontWeight: 600 }}>
                      International Registration Plan (IRP)
                    </span>
                    .{' '}
                  </li>
                  <li>
                    Process any{' '}
                    <span style={{ fontWeight: 600 }}>
                      Kansas Department of Revenue
                    </span>{' '}
                    vehicle paperwork for heavy trucks.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-schedule-1/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS-stamped Schedule 1
                  </Link>{' '}
                  is your official proof of HVUT payment and must be submitted
                  with your registration documents.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="when"
                >
                  When is the HVUT due?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The HVUT filing period runs from{' '}
                  <span style={{ fontWeight: 600 }}>July 1 to June 30</span>{' '}
                  each year, covering the full federal tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  Key deadlines:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    For vehicles in use as of{' '}
                    <span style={{ fontWeight: 600 }}>July</span>, file by{' '}
                    <span style={{ fontWeight: 600 }}>August 31*</span>.
                  </li>
                  <li>
                    For new vehicles, file by the{' '}
                    <span style={{ fontWeight: 600 }}>
                      last day of the month following the first month of use.
                    </span>
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>Example:</span> If you start
                  using your truck in October, your HVUT filing is due by{' '}
                  <span style={{ fontWeight: 600 }}>November 30</span>.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ borderLeft: '3px solid rgb(102, 60, 0)', mb: 3 }}
                >
                  *If the deadline falls on the weekend or federal holiday, it
                  is extended to the next business days.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="how"
                >
                  How to file HVUT in Kansas?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can file your HVUT using{' '}
                  <span style={{ fontWeight: 600 }}>IRS Form 2290</span> in two
                  main ways:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    fontSize: '18px !important',
                  }}
                >
                  1. E-File with an IRS-Authorized Provider
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can file online through TaxZerone—it’s the fastest and
                  most convenient method.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  Steps to file Form 2290 with TaxZerone:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <Link
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form2290`}
                      sx={{ color: '#0202ff' }}
                    >
                      Sign up
                    </Link>{' '}
                    or{' '}
                    <Link
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signin/?ref=form2290`}
                      sx={{ color: '#0202ff' }}
                    >
                      sign in
                    </Link>{' '}
                    to your TaxZerone account.
                  </li>
                  <li>Enter your business, vehicle, and EIN information.</li>
                  <li>Choose a payment method and submit it. </li>
                  <li>
                    Receive your{' '}
                    <span style={{ fontWeight: 600 }}>stamped Schedule 1</span>{' '}
                    within minutes.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    fontSize: '18px !important',
                  }}
                >
                  2. Paper Filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/f2290.pdf"
                    sx={{ color: '#0202ff' }}
                  >
                    download Form 2290
                  </Link>{' '}
                  from the IRS website, fill it out, and mail it with payment.
                  However, this method can take{' '}
                  <span style={{ fontWeight: 600 }}>several weeks</span> to
                  process.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="where"
                >
                  Where to submit proof of HVUT payment?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  After filing, you’ll receive a{' '}
                  <span style={{ fontWeight: 600 }}>
                    Schedule 1 stamped by the IRS
                  </span>
                  . This is your{' '}
                  <span style={{ fontWeight: 600 }}>proof of HVUT payment</span>
                  , which must be submitted to:
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    The{' '}
                    <span style={{ fontWeight: 600 }}>
                      Kansas Department of Revenue
                    </span>
                    .
                  </li>
                  <li>
                    The{' '}
                    <span style={{ fontWeight: 600 }}>Kansas IRP Office</span>{' '}
                    for apportioned vehicles.{' '}
                  </li>
                  <li>
                    Any{' '}
                    <span style={{ fontWeight: 600 }}>
                      Kansas County Treasurer’s Office
                    </span>{' '}
                    that handles heavy vehicle registration.
                  </li>
                </Box>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{ borderLeft: '3px solid rgb(0, 70, 102)', mb: 3 }}
                >
                  <b>Tip:</b> Most offices accept digital or printed copies of
                  the stamped Schedule 1.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="pay"
                >
                  How to Pay Kansas Highway Use Tax?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Kansas truck owners filing 2290 form can choose from these
                  IRS-approved payment methods:
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Electronic Funds Withdrawal
                    </span>{' '}
                    (
                    <Link
                      href="/resources/electronic-funds-withdrawal-efw/"
                      sx={{ color: '#0202ff' }}
                    >
                      EFW
                    </Link>
                    ) – Conveniently pay directly from your bank account while
                    e-filing.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Electronic Federal Tax Payment System
                    </span>{' '}
                    (
                    <Link href="/resources/eftps/" sx={{ color: '#0202ff' }}>
                      EFTPS
                    </Link>
                    ) – A secure IRS payment option for scheduled tax payments.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Debit or Credit Card
                    </span>{' '}
                    – Pay online through an IRS-authorized payment processor.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Check or Money Order
                    </span>{' '}
                    – Accepted only for paper-filed Form 2290 returns.
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="penalties"
                >
                  Kansas HVUT Penalties
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss the HVUT deadline or underpay:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Late Filing Penalty:
                    </span>{' '}
                    If you miss the filing deadline (August 31), the IRS may
                    charge a penalty of{' '}
                    <span style={{ fontWeight: 600 }}>
                      4.5% of the total tax due per month
                    </span>
                    , up to a maximum of{' '}
                    <span style={{ fontWeight: 600 }}>five months</span>.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      Late Payment Penalty:
                    </span>{' '}
                    If you file Form 2290 but fail to pay the tax owed, an
                    additional penalty of{' '}
                    <span style={{ fontWeight: 600 }}>
                      0.5% of the unpaid tax per month
                    </span>{' '}
                    may apply.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Interest Charges:</span>{' '}
                    In addition to penalties, the IRS may charge{' '}
                    <span style={{ fontWeight: 600 }}>
                      interest at 0.54% per month
                    </span>{' '}
                    on any unpaid tax balance.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1.5,
                    scrollMarginTop: 87,
                  }}
                  id="consequences"
                >
                  Kansas state consequences:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    Registration Suspension:
                  </span>{' '}
                  Kansas may suspend the registration of heavy vehicles if proof
                  of HVUT payment, such as an IRS-stamped Schedule 1, is not
                  submitted.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <span style={{ fontWeight: 600 }}>
                    Registration Renewal Delays:
                  </span>{' '}
                  You cannot renew your vehicle registration in Kansas without
                  providing proof that Form 2290 has been filed and the HVUT has
                  been paid.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Source & Reference
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given on this page is based on official
                        guidance from the{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i2290"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.ksrevenue.gov/dovcmv.html"
                          sx={{ color: '#0202ff' }}
                        >
                          Kansas
                        </Link>{' '}
                        <Link
                          href="https://www.ksrevenue.gov/dovcmv.html"
                          sx={{ color: '#0202ff' }}
                        >
                          Department of Revenue
                        </Link>
                        , and the{' '}
                        <Link
                          href="https://www.ksrevenue.gov/pdf/mcs74.pdf"
                          sx={{ color: '#0202ff' }}
                        >
                          Kansas IRP Manual
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>{' '}
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Don’t Wait—File Your Kansas Form 2290 Today!
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Avoid penalties, registration delays, and last-minute stress.
              E-file IRS Form 2290 with TaxZerone <br />
              and receive your stamped Schedule 1 in minutes.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=kansas-form-2290`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Kansas;
